import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/uxp-photoshop/uxp-photoshop/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "uxp-manifest-v5"
    }}>{`UXP Manifest v5`}</h1>
    <p>{`Manifest v5 gives developers access to a new plugin permissions model and WebViews in modal dialogs. Using the full manifest v5 feature set requires PS 23.3.0 or higher.  (UXP 6.0 or higher)`}</p>
    <p>{`Upgrade your plugin to use the latest manifest feature set by changing the `}<inlineCode parentName="p">{`manifestVersion`}</inlineCode>{` element. `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"manifestVersion": 5
`}</code></pre>
    <h2 {...{
      "id": "example-manifest"
    }}>{`Example manifest`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "manifestVersion": 5,
    "id": "YOUR_ID_HERE",
    "name": "Name of your plugin",
    "version": "1.0.0",
    "main": "index.html",
    "host": {
        "app": "PS",
        "minVersion": "23.3.0"
    },
    "entrypoints": [
        {
            "type": "command",
            "id": "commandFn",
            "label": {
                "default": "Show A Dialog"
            }
        },
        {
            "type": "panel",
            "id": "panelName",
            "label": {
                "default": "Panel Name"
            },
            "minimumSize": {"width": 230, "height": 200},
            "maximumSize": {"width": 2000, "height": 2000},
            "preferredDockedSize": {"width": 230, "height": 300},
            "preferredFloatingSize": {"width": 230, "height": 300},
            "icons": [
                {"width":23,"height":23,"path":"icons/dark.png","scale":[1,2],"theme":["darkest","dark","medium"]},
                {"width":23,"height":23,"path":"icons/light.png","scale":[1,2],"theme":["lightest","light"]}
            ]
        }
    ],
    "icons": [
        { "width": 23, "height": 23, "path": "icons/icon_D.png", "scale": [ 1, 2 ], "theme": [ "dark", "darkest" ], "species": [ "generic" ] },
        { "width": 23, "height": 23, "path": "icons/icon_N.png", "scale": [ 1, 2 ], "theme": [ "lightest", "light" ], "species": [ "generic" ] }
    ],
    "requiredPermissions": {
        "network": {
            "domains": [
                "https://adobe.com",         
            ]
        },
        "clipboard": "readAndWrite",
        "webview": {
            "allow": "yes",
            "domains": [ "https://*.adobe.com", "https://*.google.com"]
        },
        "launchProcess": { 
            "schemes":
                [ "https", "slack"],        
            "extensions":
                [ ".xd", ".psd" ]    
        }, 
    },
}
`}</code></pre>
    <h2 {...{
      "id": "changes-to-top-level-metadata"
    }}>{`Changes to top-level metadata`}</h2>
    <p>{`Starting from changes to top-level metadata, here are some keys that changed with the v5 upgrade. Read `}<a parentName="p" {...{
        "href": "../manifest-v4/"
      }}>{`manifest v4`}</a>{` to learn more about each key/value field.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Key path`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Change`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`version`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Version number of your plugin in `}<inlineCode parentName="td">{`x.y.z`}</inlineCode>{` format.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Plugins can specify semver format with no warnings. `}<br />{`Specify at least one number and the minor and/or patch will be autofilled with zeroes.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`requiredPermissions`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`object`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Declare `}<a parentName="td" {...{
              "href": "#plugin-permissions"
            }}>{`plugin permissions`}</a>{`.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New in v5.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`entrypoints`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`EntryPointDefinition[]`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Describes the entries your plugin adds to the `}<em parentName="td">{`Plugins`}</em>{` menu and plugin panel.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`v5 changes in next section.`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "changes-to-entry-points"
    }}>{`Changes to entry points`}</h2>
    <p>{`The changes to `}<inlineCode parentName="p">{`entryPoints`}</inlineCode>{` add flexibility in specifying the initial view/location of plugin panels.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Key`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Change`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Unique identifier for the entry point. This `}<inlineCode parentName="td">{`id`}</inlineCode>{` will also be mapped to entrypoints defined in your plugin code.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Passed as the id for the uxpcommand, uxpshowpanel, and uxphidepanel`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`title`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Title for the plugin. `}<br />{`Defaults to plugin's name if none is specified`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`New in v5.`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "plugin-permissions"
    }}>{`Plugin Permissions`}</h2>
    <p>{`Plugins using Manifest v5 will enjoy the enhancements in security with the introduction of new permissions model. Users will be asked for their consent when your plugin attempts to use `}<inlineCode parentName="p">{`openExternal`}</inlineCode>{`, `}<inlineCode parentName="p">{`openPath`}</inlineCode>{`, and `}<inlineCode parentName="p">{`sp-link`}</inlineCode>{`/anchor tags. For everything else, consent is given at install time.`}</p>
    <p>{`Starting with v5, any permissions not explicitly declared in the manifest are not granted by default.`}</p>
    <h3 {...{
      "id": "network"
    }}>{`Network`}</h3>
    <p>{`In order for our plugin to use the network, you must define domains that your plugin will access. You can do this by adding the network object to the requiredPermissions section of the manifest.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "requiredPermissions": {
        "network": {
            "domains": [
                "https://source.unsplash.com",         
            ]
        }
    }
}
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<img src='https://source.unsplash.com/random'>
`}</code></pre>
    <h3 {...{
      "id": "clipboard"
    }}>{`Clipboard`}</h3>
    <p>{`To grant read and/or write access to the system clipboard, declare it in `}<inlineCode parentName="p">{`requiredPermissions`}</inlineCode>{`.
`}<inlineCode parentName="p">{`clipboard`}</inlineCode>{` accepts:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`readAndWrite`}</inlineCode>{` for read/write access`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`read`}</inlineCode>{` for read-only access. `}</li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "requiredPermissions": {
        "clipboard": "readAndWrite"
    }
}
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const clipboard = navigator.clipboard;
const dataTransferProviders = {
    'text/plain': 'Sample text'
};
clipboard.writeText(dataTransferProviders).then(
    (result) => {...},
    (error) => {...}
);
`}</code></pre>
    <h3 {...{
      "id": "local-filesystem"
    }}>{`Local Filesystem`}</h3>
    <p><inlineCode parentName="p">{`localFileSystem`}</inlineCode>{` accepts:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`request`}</inlineCode>{`: Allows the plugin to access files on the local file system. For files outside of the plugin's data, temporary, and code folders, the user will be prompted for consent.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`plugin`}</inlineCode>{`: Allows the plugin to access the plugin's sandbox, but does not enable file picker APIs.`}</li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "requiredPermissions": {
        "localFileSystem": "request"
    }
}
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const fs = require('uxp').storage.localFileSystem;
const file = await fs.getFileForSaving('manifest_demo.txt');
await file.write('Manifest v5 demo');
`}</code></pre>
    <p>{`If `}<inlineCode parentName="p">{`plugin`}</inlineCode>{` is not specified for plugin storage, the manifest will default to that setting.`}</p>
    <h3 {...{
      "id": "launch-process"
    }}>{`Launch Process`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`launchProcess`}</inlineCode>{` permission in the manifest controls the ability to launch applications and open files in other applications. `}</p>
    <p>{`With manifest v5, the `}<inlineCode parentName="p">{`launchProcess`}</inlineCode>{` permission must be declared to use `}<inlineCode parentName="p">{`openExternal`}</inlineCode>{` or `}<inlineCode parentName="p">{`openPath`}</inlineCode>{`. `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"permissions": {        
    "launchProcess": {    
        // allows launching files with specified URI schemes         
        "schemes": 
            [ "https", "slack", "adbxd" ],   
        // allows opening files with the specified file extensions          
        "extensions": 
            [ ".pdf", ".xd", ".psd" ],        
    },   
}
`}</code></pre>
    <p>{`Both `}<inlineCode parentName="p">{`openPath`}</inlineCode>{` and `}<inlineCode parentName="p">{`openExternal`}</inlineCode>{` rely on this permission set, and upon either function call, the user will get a runtime consent dialog. Only after they agree will the API call execute. `}</p>
    <h3 {...{
      "id": "plugin-communication"
    }}>{`Plugin communication`}</h3>
    <p>{`If enabled, the plugin can communicate with other installed plugins. Defaults to `}<inlineCode parentName="p">{`false`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"permissions": {        
    "ipc": {
        "enablePluginCommunication": true   
    }
}
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const { pluginManager } = require("uxp");
// find the Alchemist plugin in the loaded plugins
const alchemistPlugin = Array.from(pluginManager.plugins).find(plugin => plugin.id==="2bcdb900");   
//What commands and panels are available?
const alchemistCommands = Array.from(alchemistPlugin.manifest.commands, command => command.commandId); // result: ["resetStateFn"]
const alchemistPanels = Array.from(alchemistPlugin.manifest.panels, command => command.panelId); // result: ["inspector"]     
// Show the inspector panel; note that panels can only be made visible -- you can't ask to hide the panel
alchemistPlugin.showPanel("inspector");
// Reset Alchemists state... but be sure you want to do this!
alchemistPlugin.invokeCommand("resetStateFn");
`}</code></pre>
    <h2 {...{
      "id": "webviews"
    }}>{`WebViews`}</h2>
    <p>{`WebViews are available with UXP 6.0, and need to be configured in your plugin's manifest (v5 required).`}</p>
    <p>{`In your `}<inlineCode parentName="p">{`manifest.json`}</inlineCode>{`: `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "manifestVersion": 5,
    "requiredPermissions": {
        "webview": {
            "allow": "yes",
            // domains --> string[] | "all"
            "domains": [ "https://*.adobe.com", "https://*.google.com"],
        }
    }
}
`}</code></pre>
    <p>{`Then in your plugin's code: `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<dialog>
    <webview id="webview" width="100%" height="360px" src="https://www.adobe.com"></webview>
</dialog>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`document.querySelector("dialog").showModal();
`}</code></pre>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "56.875%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/10d65d9329ad366e8bfc3f686fbddea6/cb523/webview_example.webp 320w", "/photoshop/uxp/static/10d65d9329ad366e8bfc3f686fbddea6/797b9/webview_example.webp 640w", "/photoshop/uxp/static/10d65d9329ad366e8bfc3f686fbddea6/4b075/webview_example.webp 1280w", "/photoshop/uxp/static/10d65d9329ad366e8bfc3f686fbddea6/f3ff0/webview_example.webp 1920w", "/photoshop/uxp/static/10d65d9329ad366e8bfc3f686fbddea6/a662b/webview_example.webp 2560w", "/photoshop/uxp/static/10d65d9329ad366e8bfc3f686fbddea6/5d562/webview_example.webp 3456w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/static/10d65d9329ad366e8bfc3f686fbddea6/72799/webview_example.png 320w", "/photoshop/uxp/static/10d65d9329ad366e8bfc3f686fbddea6/6af66/webview_example.png 640w", "/photoshop/uxp/static/10d65d9329ad366e8bfc3f686fbddea6/21b4d/webview_example.png 1280w", "/photoshop/uxp/static/10d65d9329ad366e8bfc3f686fbddea6/29114/webview_example.png 1920w", "/photoshop/uxp/static/10d65d9329ad366e8bfc3f686fbddea6/c2d13/webview_example.png 2560w", "/photoshop/uxp/static/10d65d9329ad366e8bfc3f686fbddea6/f7171/webview_example.png 3456w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/photoshop/uxp/static/10d65d9329ad366e8bfc3f686fbddea6/21b4d/webview_example.png",
            "alt": "Webview",
            "title": "Webview",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h3 {...{
      "id": "limitations"
    }}>{`Limitations`}</h3>
    <p>{`WebViews are available within modal dialogs only for now.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      